import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableV3Ref, TableV3Filters } from 'common/table/components/TableV3';
import { TableV3FilterItem } from 'common/table/types/TableV3';
import { CandidateProfileTableColumns } from 'modules/candidate-profile/tables/types/CandidateProfileTable';
import {
  CandidateFilterActiveModes,
  getActiveMode,
  getSelectedValues,
  isSelection,
  removeCondition,
  setModeCondition,
  toTableFilterValues
} from 'modules/candidate-profile/tables/filters/candidateFilterState';
import { hasTableV3Filters } from 'common/table/utils/hasTableV3Filters';
import { syncConditionsFromTable } from 'modules/candidate-profile/tables/filters/syncConditionsFromTable';
import {
  ParsedCandidateFilterTree,
  activeModesForConditions,
  parseCandidateFilterTree
} from 'modules/candidate-profile/tables/filters/parseCandidateFilterTree';
import {
  applySearchTerms,
  fitsSearchInput,
  searchColumnForField,
  searchFieldsOf
} from 'modules/candidate-profile/tables/filters/searchesToConditions';
import {
  DismissedFilter,
  isAddedBackBy,
  isDismissedBy,
  removedFilters,
  toDismissedFilter
} from 'modules/candidate-profile/tables/filters/dismissedFilters';
import {
  asSearchFields,
  mergeReply,
  withoutFields
} from 'modules/candidate-profile/tables/filters/mergeConditions';
import {
  CandidateFilterGroups,
  CandidateFilterMatchMode,
  CandidateFilterNode,
  CandidateFilterState,
  SourcingFilterField
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import {
  joinChipGroupsAt,
  removeMissingFromGroups,
  splitChipGroupAt
} from 'modules/candidate-profile/tables/filters/candidateFilterGroups';

export type MergeAppliedFilters = (
  tree: CandidateFilterNode | null | undefined,
  removeFields?: string[],
  sentConditions?: CandidateFilterState[],
  sentGroups?: CandidateFilterGroups
) => CandidateFilterState[] | null;

type UseCandidateFilterConditionsParams = {
  availableFilters: SourcingFilterField[];
  tableRef: React.RefObject<TableV3Ref<CandidateProfileTableColumns>>;
};

type FilterConditionsState = {
  conditions: CandidateFilterState[];
  activeModes: CandidateFilterActiveModes;
  groups: CandidateFilterGroups;
  // Custom-field columns reported before their configs load.
  passthrough: {
    filterValues: Record<string, TableV3FilterItem[]>;
    searchFields: Record<string, string>;
  };
};

const EMPTY_STATE: FilterConditionsState = {
  conditions: [],
  activeModes: {},
  groups: [],
  passthrough: { filterValues: {}, searchFields: {} }
};

const EMPTY_TREE: ParsedCandidateFilterTree = {
  conditions: [],
  activeModes: {},
  groups: [],
  unsupportedCount: 0
};

type TableFilters = TableV3Filters<CandidateProfileTableColumns>;

const buildTableFilters = (
  state: FilterConditionsState,
  availableFilters: SourcingFilterField[]
): TableFilters => {
  const columnConditions = state.conditions.filter(
    (condition) => !searchColumnForField(condition.field, availableFilters)
  );

  return {
    searchFields: asSearchFields({
      ...state.passthrough.searchFields,
      ...searchFieldsOf(state.conditions, availableFilters)
    }),
    filterValues: {
      ...state.passthrough.filterValues,
      ...toTableFilterValues(columnConditions, state.activeModes, {
        rangeFor: (field) =>
          availableFilters.find((available) => available.field === field)?.range
      })
    }
  } as TableFilters;
};

const readTable = (
  current: FilterConditionsState,
  next: TableFilters,
  availableFilters: SourcingFilterField[]
) => {
  const synced = syncConditionsFromTable({
    conditions: current.conditions,
    filterValues: (next.filterValues ?? {}) as Record<
      string,
      TableV3FilterItem[]
    >,
    activeModes: current.activeModes,
    availableFilters
  });
  const terms = applySearchTerms(
    synced.conditions,
    next.searchFields,
    availableFilters
  );
  const heldByConditions = new Set(
    current.conditions.map((condition) => condition.field)
  );

  return {
    conditions: terms.conditions,
    passthrough: {
      filterValues: Object.fromEntries(
        Object.entries(synced.passthrough).filter(
          ([field]) => !heldByConditions.has(field)
        )
      ),
      searchFields: terms.unreadable
    }
  };
};

const serializeFilterValuesForComparison = (
  filterValues: TableFilters['filterValues']
) =>
  Object.entries((filterValues ?? {}) as Record<string, TableV3FilterItem[]>)
    .filter(([, items]) => items?.length)
    .map(([field, items]) =>
      [field, ...items.map((item) => item.value).sort()].join('\u0000')
    )
    .sort()
    .join('\n');

// A search input holds one term, so typing any other term puts the column back.
const withoutRemovedValuesOnSearchColumns = (
  dismissed: DismissedFilter[],
  availableFilters: SourcingFilterField[]
): DismissedFilter[] =>
  dismissed.map(({ removedValues, ...rest }) =>
    searchColumnForField(rest.field, availableFilters)
      ? rest
      : { ...rest, removedValues }
  );

const unreadCount = (passthrough: FilterConditionsState['passthrough']) =>
  Object.keys(passthrough.filterValues).length +
  Object.keys(passthrough.searchFields).length;

export const useCandidateFilterConditions = ({
  availableFilters,
  tableRef
}: UseCandidateFilterConditionsParams) => {
  const [state, setState] = useState<FilterConditionsState>(EMPTY_STATE);

  // Callbacks that reach the table as column definitions keep their identity,
  // or new columns rebuild the headers and close an open dropdown.
  const stateRef = useRef(state);

  // The grid captures a header's column definition when the header is
  // created, so a mounted mode toggle subscribes to the modes instead.
  const modeListenersRef = useRef(new Set<() => void>());

  const subscribeToModes = useCallback((listener: () => void) => {
    modeListenersRef.current.add(listener);
    return () => {
      modeListenersRef.current.delete(listener);
    };
  }, []);

  // applyFilters reports back before React re-renders, so the ref must move
  // with the state or the report is read against the previous one.
  const commit = useCallback((next: FilterConditionsState) => {
    stateRef.current = next;
    setState(next);
    modeListenersRef.current.forEach((listener) => listener());
  }, []);

  const availableFiltersRef = useRef(availableFilters);
  availableFiltersRef.current = availableFilters;

  const isSearchColumn = (field: string) =>
    Boolean(searchColumnForField(field, availableFiltersRef.current));

  const dismissedRef = useRef<DismissedFilter[]>([]);

  const dismiss = (filters: DismissedFilter[]) => {
    dismissedRef.current = [
      ...dismissedRef.current,
      ...withoutRemovedValuesOnSearchColumns(
        filters,
        availableFiltersRef.current
      )
    ];
  };

  const recordRecruiterChange = useCallback(
    (before: CandidateFilterState[], after: CandidateFilterState[]) => {
      const addedBack = (dismissed: DismissedFilter) =>
        after.some((condition) => isAddedBackBy(dismissed, condition));

      dismissedRef.current = [
        ...dismissedRef.current.filter((dismissed) => !addedBack(dismissed)),
        ...withoutRemovedValuesOnSearchColumns(
          removedFilters(before, after),
          availableFiltersRef.current
        )
      ];
    },
    []
  );

  const clearDismissed = () => {
    dismissedRef.current = [];
  };

  const isDismissed = (condition: CandidateFilterState) =>
    isDismissedBy(dismissedRef.current, condition);

  const isOwnTableUpdateRef = useRef(false);
  const heldByTableRef = useRef<TableFilters>({});

  const runOwnTableUpdate = useCallback((update: () => void) => {
    isOwnTableUpdateRef.current = true;
    try {
      update();
    } finally {
      isOwnTableUpdateRef.current = false;
    }
  }, []);

  const pushToTable = useCallback(
    (next: FilterConditionsState) => {
      const filters = buildTableFilters(next, availableFiltersRef.current);
      heldByTableRef.current = filters;
      runOwnTableUpdate(() => tableRef.current?.applyFilters(filters));
    },
    [runOwnTableUpdate, tableRef]
  );

  const clearTable = () => {
    heldByTableRef.current = {};
    runOwnTableUpdate(() => tableRef.current?.clearFilters());
  };

  const handleTableFiltersChanged = useCallback(
    (next: TableFilters) => {
      heldByTableRef.current = next;
      if (isOwnTableUpdateRef.current) return;

      const current = stateRef.current;
      const read = readTable(current, next, availableFiltersRef.current);
      recordRecruiterChange(current.conditions, read.conditions);

      commit({
        ...current,
        ...read,
        groups: removeMissingFromGroups(read.conditions, current.groups)
      });
    },
    [commit, recordRecruiterChange]
  );

  // Not on availableFilters: a new array every render would be a new search.
  const tableFilters = useMemo(
    () => buildTableFilters(state, availableFiltersRef.current),
    [state]
  );

  // Custom-field configs land late.
  useEffect(() => {
    const current = stateRef.current;
    const filters = buildTableFilters(current, availableFilters);
    const read = readTable(current, filters, availableFilters);
    const unreadChanged =
      unreadCount(read.passthrough) !== unreadCount(current.passthrough);
    const moved = Object.keys(filters.searchFields ?? {}).some(
      (field) => !(field in (heldByTableRef.current.searchFields ?? {}))
    );
    if (!unreadChanged && !moved) return;

    const next = { ...current, ...read };
    commit(next);
    if (moved) pushToTable(next);
  }, [availableFilters, commit, pushToTable, tableFilters]);

  // Push whenever the state's column values differ from what the table holds.
  useEffect(() => {
    const wanted = buildTableFilters(stateRef.current, availableFilters);
    if (
      serializeFilterValuesForComparison(wanted.filterValues) ===
      serializeFilterValuesForComparison(heldByTableRef.current.filterValues)
    )
      return;
    pushToTable(stateRef.current);
  }, [availableFilters, pushToTable, state]);

  const setMatchMode = useCallback(
    (
      field: string,
      mode: CandidateFilterMatchMode,
      options?: { push?: boolean }
    ) => {
      const current = stateRef.current;
      if (getActiveMode(current.activeModes, field) === mode) return;

      const next = {
        ...current,
        activeModes: { ...current.activeModes, [field]: mode }
      };
      commit(next);
      if (options?.push === false) return;
      pushToTable(next);
    },
    [commit, pushToTable]
  );

  // Un-pushed: the apply that follows is the only search.
  const setModeValues = useCallback(
    (
      field: string,
      mode: CandidateFilterMatchMode,
      items: TableV3FilterItem[]
    ) => {
      const available = availableFiltersRef.current.find(
        (each) => each.field === field
      );
      if (!available) return;

      const current = stateRef.current;
      const conditions = setModeCondition(
        current.conditions,
        available,
        mode,
        items
      );
      recordRecruiterChange(current.conditions, conditions);
      commit({ ...current, conditions });
    },
    [commit, recordRecruiterChange]
  );

  const getMatchMode = useCallback(
    (field: string) => getActiveMode(stateRef.current.activeModes, field),
    []
  );

  const getValuesForMode = useCallback(
    (field: string, mode: CandidateFilterMatchMode) =>
      getSelectedValues(stateRef.current.conditions, field, mode),
    []
  );

  const removeFilterCondition = (
    field: string,
    mode: CandidateFilterMatchMode,
    id?: string
  ) => {
    const current = stateRef.current;
    const removed = id
      ? current.conditions.find((condition) => condition.id === id)
      : undefined;
    const hasSibling = current.conditions.some(
      (condition) =>
        condition !== removed &&
        condition.field === field &&
        'mode' in condition &&
        condition.mode === mode
    );

    const remaining = removeCondition(current.conditions, field, mode, id);
    const removedValues = current.conditions
      .filter((condition) => !remaining.includes(condition))
      .flatMap((condition) => (isSelection(condition) ? condition.values : []));

    dismiss([
      {
        field,
        mode,
        removedValues,
        ...(hasSibling && removed && isSelection(removed)
          ? { values: removed.values }
          : {})
      }
    ]);

    const next = {
      ...current,
      conditions: remaining,
      groups: removeMissingFromGroups(remaining, current.groups)
    };
    commit(next);
    pushToTable(next);
  };

  const commitTree = (
    { conditions, activeModes, groups }: ParsedCandidateFilterTree,
    options?: {
      merge?: boolean;
      removeFields?: Set<string>;
      sent?: CandidateFilterState[];
      sentGroups?: CandidateFilterGroups;
    }
  ) => {
    const current = stateRef.current;
    const removed = options?.removeFields ?? new Set<string>();

    const rail = options?.merge
      ? {
          conditions: withoutFields(current.conditions, removed),
          groups: current.groups
        }
      : { conditions: [], groups: [] };
    const sent = options?.sent && {
      conditions: options.sent,
      groups: options.sentGroups ?? []
    };
    const merged = mergeReply(
      rail,
      { conditions, groups },
      sent,
      isSearchColumn
    );

    const next: FilterConditionsState = {
      conditions: merged.conditions,
      groups: removeMissingFromGroups(merged.conditions, merged.groups),
      // The rail's own mode wins.
      activeModes: options?.merge
        ? { ...activeModes, ...current.activeModes }
        : activeModes,
      passthrough: options?.merge
        ? current.passthrough
        : EMPTY_STATE.passthrough
    };

    commit(next);
    clearTable();
    pushToTable(next);
  };

  const restoreFromTree = (tree: CandidateFilterNode | null | undefined) => {
    const parsed = parseCandidateFilterTree(tree);

    if (parsed.unsupportedCount > 0) {
      console.warn(
        `Reopened search: ${parsed.unsupportedCount} filter condition(s) could ` +
          'not be restored into the filter panel.'
      );
    }

    commitTree(parsed);
  };

  const mergeAppliedFilters: MergeAppliedFilters = (
    tree,
    removeFields,
    sentConditions,
    sentGroups
  ) => {
    const removed = new Set(removeFields ?? []);
    if (!tree && !removed.size) return stateRef.current.conditions;

    const isKept = (condition: CandidateFilterState) =>
      !removed.has(condition.field) && !isDismissed(condition);

    const applied = parseCandidateFilterTree(tree);
    const keptConditions = applied.conditions.filter(isKept);
    const kept: ParsedCandidateFilterTree = {
      ...applied,
      conditions: keptConditions,
      activeModes: activeModesForConditions(keptConditions)
    };

    // A search input holds one term: a reply asking more of a text column,
    // outside an Or group, is refused rather than shown narrower than it ran.
    const grouped = new Set(kept.groups.flat());
    const searched = keptConditions.filter(
      (condition) =>
        !grouped.has(condition.id) &&
        isSelection(condition) &&
        isSearchColumn(condition.field)
    );
    const fitting = searched.filter(fitsSearchInput);
    const inputCannotHold =
      searched.some(
        (condition) =>
          isSelection(condition) &&
          condition.mode !== CandidateFilterMatchMode.INCLUDE_ANY
      ) ||
      fitting.some(
        (condition, at) =>
          fitting.findIndex((other) => other.field === condition.field) !== at
      );

    if (kept.unsupportedCount > 0 || inputCannotHold) {
      console.warn(
        'Search filters: this search used conditions that no chip can show ' +
          'exactly, so none are shown for it.'
      );
      if (removed.size) {
        commitTree(EMPTY_TREE, { merge: true, removeFields: removed });
      }
      return null;
    }

    commitTree(kept, {
      merge: true,
      removeFields: removed,
      sent: sentConditions?.filter(isKept),
      sentGroups
    });

    const hidesAppliedFilter =
      keptConditions.length < applied.conditions.length;
    return hidesAppliedFilter ? null : stateRef.current.conditions;
  };

  const removeFieldFilters = (field: string) => {
    const current = stateRef.current;

    dismiss(
      current.conditions
        .filter((condition) => condition.field === field)
        .map(toDismissedFilter)
    );

    const conditions = current.conditions.filter(
      (condition) => condition.field !== field
    );
    const next: FilterConditionsState = {
      ...current,
      conditions,
      groups: removeMissingFromGroups(conditions, current.groups)
    };
    commit(next);
    clearTable();
    pushToTable(next);
  };

  const joinChipGroups = (groupIndex: number) => {
    const current = stateRef.current;
    const next = {
      ...current,
      groups: joinChipGroupsAt(current.conditions, current.groups, groupIndex)
    };
    commit(next);
    pushToTable(next);
  };

  const splitChipGroup = (groupIndex: number, chipIndex: number) => {
    const current = stateRef.current;
    const next = {
      ...current,
      groups: splitChipGroupAt(
        current.conditions,
        current.groups,
        groupIndex,
        chipIndex
      )
    };
    commit(next);
    pushToTable(next);
  };

  const clearAll = () => {
    clearDismissed();
    // Modes are kept: clearing values should not move a column back to Include any.
    commit({ ...EMPTY_STATE, activeModes: stateRef.current.activeModes });
    clearTable();
  };

  const hasFilters = hasTableV3Filters(tableFilters);

  return {
    conditions: state.conditions,
    activeModes: state.activeModes,
    groups: state.groups,
    joinChipGroups,
    splitChipGroup,
    tableFilters,
    hasFilters,
    handleTableFiltersChanged,
    setMatchMode,
    setModeValues,
    getMatchMode,
    subscribeToModes,
    getValuesForMode,
    removeFilterCondition,
    searchFields: tableFilters.searchFields,
    restoreFromTree,
    mergeAppliedFilters,
    removeFieldFilters,
    clearDismissed,
    clearAll
  };
};
